import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../api';
import type { InviteInfo } from '../api/types';
import { Icon, IconName } from '../components/ui';
import './Auth.css';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = useMemo(() => searchParams.get('invite') || '', [searchParams]);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    portal_name: '',
    password: '',
    confirm_password: '',
    invite_token: inviteToken,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1); // 2-step form
  const [inviteLoading, setInviteLoading] = useState(Boolean(inviteToken));
  // Typed from the API schema. The hand-written shape declared `role: string` and a
  // `hasAccount` field the endpoint does not return.
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);

  const isInviteMode = Boolean(inviteToken);
  // `hasAccount` is now declared on InviteInfoSerializer, so the generated type carries it
  // and this branch is live. The endpoint always returned the field; the schema did not
  // declare it, so the client type omitted it and this was pinned to false — which showed
  // an invitee who already had an account the "set your password" form, on a link that
  // could only ever answer 400.
  const invitedUserAlreadyExists = Boolean(inviteInfo?.hasAccount);

  useEffect(() => {
    let active = true;

    if (!inviteToken) {
      setInviteLoading(false);
      return undefined;
    }

    setInviteLoading(true);
    authAPI.inviteInfo(inviteToken)
      .then((res) => {
        if (!active) return;
        const info = res.data;
        setInviteInfo(info);
        setForm((prev) => ({
          ...prev,
          email: info.email || prev.email,
          portal_name: info.portal || prev.portal_name,
          invite_token: inviteToken,
        }));
        setErrors((prev) => ({ ...prev, general: '' }));
      })
      .catch((err: any) => {
        if (!active) return;
        const message = err?.response?.data?.error || 'This invite link is invalid or has expired.';
        setErrors({ general: message });
      })
      .finally(() => {
        if (active) setInviteLoading(false);
      });

    return () => {
      active = false;
    };
  }, [inviteToken]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (inviteInfo && (name === 'email' || name === 'portal_name')) return;

    setForm({ ...form, [name]: value });
    if (name === 'full_name' && !inviteInfo) {
      const slug = value.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
      setForm((prev) => ({ ...prev, full_name: value, portal_name: slug }));
    }
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateStep1 = () => {
    const errs: Record<string, string> = {};
    if (!form.full_name.trim()) errs.full_name = 'Full name is required.';
    if (!form.email.trim()) errs.email = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Enter a valid email.';
    if (!form.portal_name.trim() && !isInviteMode) errs.portal_name = 'Portal name is required.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep2 = () => {
    const errs: Record<string, string> = {};
    if (!form.password) errs.password = 'Password is required.';
    // 10, not 8: AUTH_PASSWORD_VALIDATORS sets MinimumLengthValidator min_length to 10
    // (backend/core/settings.py:275). Advertising 8 let a compliant-looking password pass
    // this form and then be rejected by the server at the last step of accepting an
    // invitation, which is the worst possible moment to find out.
    else if (form.password.length < 10) errs.password = 'Password must be at least 10 characters.';
    if (form.password !== form.confirm_password) errs.confirm_password = 'Passwords do not match.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;
    setLoading(true);
    try {
      await register(form);
      navigate('/portal/dashboard');
    } catch (err: any) {
      const apiErrors = err?.response?.data;
      if (typeof apiErrors === 'object') {
        const mapped: Record<string, string> = {};
        Object.entries(apiErrors).forEach(([k, v]) => {
          mapped[k] = Array.isArray(v) ? (v as string[]).join(' ') : String(v);
        });
        setErrors(mapped);
        if (mapped.email || mapped.portal_name) setStep(1);
      } else {
        setErrors({ general: 'Registration failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page auth-page-register">
      <div className="auth-left">
        <div className="auth-brand">
          <Link to="/" className="auth-logo">
            <div className="auth-logo-icon">
              <svg width="32" height="32" viewBox="0 0 28 28" fill="none">
                <rect width="12" height="12" rx="3" fill="#444ce7" />
                <rect x="16" width="12" height="12" rx="3" fill="#f25c2b" />
                <rect y="16" width="12" height="12" rx="3" fill="#3b6df8" />
                <rect x="16" y="16" width="12" height="12" rx="3" fill="#a855f7" />
              </svg>
            </div>
            <div>
              <div className="auth-logo-brand">ROOMAN</div>
              <div className="auth-logo-product">Workspace</div>
            </div>
          </Link>
        </div>

        <div className="auth-left-content">
          <div className="register-features">
            {([
              { icon: 'tasks', title: 'Task Management', desc: 'Organize work with lists, boards, and Gantt charts' },
              { icon: 'clock', title: 'Time Tracking', desc: 'Log hours manually or with automatic timers' },
              { icon: 'chart', title: 'Reports', desc: 'Deep insights into project health and team performance' },
              { icon: 'users', title: 'Collaboration', desc: 'Chat, share documents, and stay in sync' },
            ] as { icon: IconName; title: string; desc: string }[]).map((f) => (
              <div key={f.title} className="reg-feature">
                <span className="reg-feature-icon"><Icon name={f.icon} size={20} /></span>
                <div>
                  <div className="reg-feature-title">{f.title}</div>
                  <div className="reg-feature-desc">{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="auth-plan-badge">
            <span className="plan-icon"><Icon name="sparkle" size={16} /></span>
            Free forever · No credit card required
          </div>
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-form-container">
          <div className="auth-form-header">
            <h1 className="auth-form-title">{isInviteMode ? 'Join workspace' : 'Create your account'}</h1>
            <p className="auth-form-sub">
              Already have an account?{' '}
              <Link
                to={inviteToken
                  ? `/login?invite=${encodeURIComponent(inviteToken)}&email=${encodeURIComponent(inviteInfo?.email || form.email || '')}`
                  : '/login'}
                className="auth-link"
              >
                Sign in
              </Link>
            </p>
          </div>

          {/* Step indicator */}
          <div className="step-indicator">
            <div className={`step ${step >= 1 ? 'step-active' : ''} ${step > 1 ? 'step-done' : ''}`}>
              <span className="step-num">{step > 1 ? <Icon name="check" size={13} strokeWidth={3} /> : '1'}</span>
              <span className="step-label">Your info</span>
            </div>
            <div className="step-line" />
            <div className={`step ${step >= 2 ? 'step-active' : ''}`}>
              <span className="step-num">2</span>
              <span className="step-label">Password</span>
            </div>
          </div>

          {errors.general && (
            <div className="auth-error">
              <span className="auth-error-icon"><Icon name="warning" size={14} /></span>
              {errors.general}
            </div>
          )}

          {inviteLoading && (
            <div className="auth-form-sub" style={{ marginBottom: 16 }}>
              Checking your invite…
            </div>
          )}

          {inviteInfo && (
            <div className="auth-notice">
              <span className="auth-error-icon">{invitedUserAlreadyExists ? <Icon name="arrowRight" size={14} /> : <Icon name="checkCircle" size={14} />}</span>
              {invitedUserAlreadyExists
                ? `This email already has an account. Sign in to join ${inviteInfo.portalName} as ${inviteInfo.role}.`
                : `You are invited to join ${inviteInfo.portalName} as ${inviteInfo.role}. Set your password to finish your account.`}
            </div>
          )}

          {invitedUserAlreadyExists ? (
            <div className="form-row-buttons">
              <Link
                to={`/login?invite=${encodeURIComponent(inviteToken)}&email=${encodeURIComponent(inviteInfo?.email || '')}`}
                className="btn-submit btn-submit-flex"
                style={{ textDecoration: 'none', justifyContent: 'center' }}
              >
                Continue to Sign in
              </Link>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="auth-form">
            {step === 1 && (
              <>
                <div className="form-group">
                  <label className="form-label" htmlFor="register-full-name">Full name</label>
                  <input id="register-full-name"
                    type="text"
                    name="full_name"
                    value={form.full_name}
                    onChange={handleChange}
                    className={`form-input ${errors.full_name ? 'form-input-error' : ''}`}
                    placeholder="Manjunath D"
                    autoFocus
                  />
                  {errors.full_name && <span className="form-error-msg">{errors.full_name}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="register-work-email">Work email</label>
                  <input id="register-work-email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    className={`form-input ${errors.email ? 'form-input-error' : ''}`}
                    placeholder="you@rooman.net"
                    readOnly={Boolean(inviteInfo)}
                  />
                  {errors.email && <span className="form-error-msg">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="register-portal-name">Portal name</label>
                  <div className="form-input-addon">
                    <input id="register-portal-name"
                      type="text"
                      name="portal_name"
                      value={form.portal_name}
                      onChange={handleChange}
                      className={`form-input ${errors.portal_name ? 'form-input-error' : ''}`}
                      placeholder="roomantechnologies"
                      readOnly={Boolean(inviteInfo)}
                      disabled={Boolean(inviteInfo)}
                    />
                    <span className="form-addon-suffix">.project.rooman.com</span>
                  </div>
                  {errors.portal_name && <span className="form-error-msg">{errors.portal_name}</span>}
                  <span className="form-hint">{inviteInfo ? 'This workspace was preselected from your invite.' : 'This will be your unique portal URL'}</span>
                </div>

                <button type="button" className="btn-submit" onClick={handleNext} disabled={inviteLoading}>
                  Continue →
                </button>
              </>
            )}

            {step === 2 && (
              <>
                <div className="form-group">
                  <label className="form-label" htmlFor="register-password">Password</label>
                  <div className="form-input-wrapper">
                    <input id="register-password"
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={form.password}
                      onChange={handleChange}
                      className={`form-input ${errors.password ? 'form-input-error' : ''}`}
                      placeholder="Minimum 10 characters"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="form-eye"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      tabIndex={-1}
                    >
                      <Icon name={showPassword ? 'eyeOff' : 'eye'} size={18} />
                    </button>
                  </div>
                  {errors.password && <span className="form-error-msg">{errors.password}</span>}
                  {form.password && (
                    <div className="password-strength">
                      <div className={`strength-bar ${form.password.length >= 8 ? 'strength-ok' : 'strength-weak'}`} />
                      <div className={`strength-bar ${form.password.length >= 10 && /[A-Z]/.test(form.password) ? 'strength-ok' : ''}`} />
                      <div className={`strength-bar ${form.password.length >= 12 && /[^a-zA-Z0-9]/.test(form.password) ? 'strength-ok' : ''}`} />
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="register-confirm-password">Confirm password</label>
                  <input id="register-confirm-password"
                    type="password"
                    name="confirm_password"
                    value={form.confirm_password}
                    onChange={handleChange}
                    className={`form-input ${errors.confirm_password ? 'form-input-error' : ''}`}
                    placeholder="Repeat your password"
                  />
                  {errors.confirm_password && <span className="form-error-msg">{errors.confirm_password}</span>}
                </div>

                <div className="form-row-buttons">
                  <button type="button" className="btn-back" onClick={() => setStep(1)}>
                    ← Back
                  </button>
                  <button
                    type="submit"
                    className={`btn-submit btn-submit-flex ${loading ? 'btn-loading' : ''}`}
                    disabled={loading}
                  >
                    {loading ? <span className="btn-spinner" /> : 'Create account'}
                  </button>
                </div>
              </>
            )}
          </form>
          )}

          <p className="auth-footer-note">
            By creating an account, you agree to our{' '}
            <button type="button" className="auth-link auth-link-button">Terms</button> and{' '}
            <button type="button" className="auth-link auth-link-button">Privacy Policy</button>.
          </p>
        </div>
      </div>
    </div>
  );
}
